import * as React from 'react';
import gql from 'graphql-tag';
import { graphql } from 'react-apollo';
import { SkeletonLoader } from '../../components/loading/SkeletonLoader';
import { RvPartyInfoTable } from './PartyInfoTable';
import glamorous from 'glamorous';
import { Button, ExpansionPanel, ExpansionPanelSummary, Typography, ExpansionPanelDetails } from '@material-ui/core';
import UnfoldMoreOutlinedIcon from '@material-ui/icons/UnfoldMoreOutlined';
import UnfoldLessOutlinedIcon from '@material-ui/icons/UnfoldLessOutlined';
import BusinessOutlinedIcon from '@material-ui/icons/BusinessOutlined';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import PeopleOutlineIcon from '@material-ui/icons/PeopleOutline';
import { PanelIcon } from '../../Dashboard/Component/Panel';
import { AnimatedItem } from '../../components/animations/AnimatedItem';
import { MATTER_DETAILS_REFRESH_EVENT } from '../MatterDetails';
import EventSystem from '../../EventSystem';

const PanelDetails = glamorous(ExpansionPanelDetails) ({
    // minHeight: '440px !important'
});

// tslint:disable-next-line:no-any
const RelationshipWrapper = glamorous.div<{ theme?: any }>((props) => ({
    paddingBottom: '10px'
}));
const PanelTitle = glamorous(Typography) ({
    display: 'flex !important',
    alignItems: 'center !important',
    fontSize: '14px !important',
    gap: '10px',
});

// Same section-header icon chip EnquiryFormDrawer.tsx uses for its own accordion sections
// (28px rounded-square, accent bg/fg) - explicit request for consistency, replacing the old
// office-ui-fabric-react Icon (no chip, no colour) this panel used before.
const PanelIconChip = glamorous.span({
    width: 28,
    height: 28,
    minWidth: 28,
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    backgroundColor: 'var(--rv-accent-indigo-bg)',
    color: 'var(--rv-accent-indigo)',
});

// Explicit request: the relationship type (e.g. "DebtorForAccounting") should stand out from
// the party's name next to it, rather than reading as one plain run of text smaller than the
// party name shown once the panel is expanded (PartyCard's own title is 15.5px/700 - see
// PartyInfoPanel.tsx). A small badge in the same indigo accent this tab's own sidebar icon
// uses, plus bumping the name itself up to match, fixes that inverted hierarchy.
const RelationshipTypeChip = glamorous.span({
    display: 'inline-flex',
    alignItems: 'center',
    flexShrink: 0,
    padding: '3px 9px',
    borderRadius: 6,
    fontSize: '11.5px',
    fontWeight: 700,
    backgroundColor: 'var(--rv-accent-indigo-bg)',
    color: 'var(--rv-accent-indigo)',
});

const PartyDescriptionText = glamorous.span({
    fontSize: '14px',
    fontWeight: 600,
});

// Inserts a space before each capital letter that follows a lowercase one, so a raw code like
// "DebtorForAccounting" reads as "Debtor For Accounting" instead of one run-together word.
function humanizeRelationshipCode(code: string): string {
    return code ? code.replace(/([a-z])([A-Z])/g, '$1 $2') : code;
}

// Same Expand all/Collapse all toggle button already established in AdminDashboard.tsx (icon +
// label pattern) - Parties.tsx has no page-shell toolbar slot to put it in (unlike
// AdminDashboard's PageContentLayout toolbarExtras), so this is a plain row above the cards.
const ExpandToggleWrapper = glamorous.div({
    display: 'flex',
    justifyContent: 'flex-start',
    marginBottom: '8px',
});

interface PartiesInfoProps {
    matterId: number | string;
    data: Data;
}

interface Data {
    loading?: boolean;
    networkStatus?: number;
    matter: {
        // tslint:disable-next-line:no-any
        relationships: any;
    };
    refetch: () => void;
}

interface PartiesInfoState {
    expandedIndices: { [index: number]: boolean };
}

export class PartiesInfo extends React.Component<PartiesInfoProps, PartiesInfoState>  {

    constructor(props: PartiesInfoProps) {
        super(props);
        this.state = { expandedIndices: {} };
        this.toggleExpanded = this.toggleExpanded.bind(this);
        this.toggleExpandAll = this.toggleExpandAll.bind(this);
    }

    componentDidMount() {
        EventSystem.on(MATTER_DETAILS_REFRESH_EVENT, this.onRefresh);
    }

    componentWillUnmount() {
        EventSystem.event.removeListener(MATTER_DETAILS_REFRESH_EVENT, this.onRefresh);
    }

    private onRefresh = () => {
        this.props.data.refetch();
    }

    private toggleExpanded(index: number) {
        this.setState(prevState => ({
            expandedIndices: {
                ...prevState.expandedIndices,
                [index]: !prevState.expandedIndices[index],
            },
        }));
    }

    private isAllExpanded(relationshipCount: number): boolean {
        if (relationshipCount === 0) {
            return false;
        }
        for (let index = 0; index < relationshipCount; index++) {
            if (!this.state.expandedIndices[index]) {
                return false;
            }
        }
        return true;
    }

    private toggleExpandAll(relationshipCount: number) {
        const isAllExpanded = this.isAllExpanded(relationshipCount);
        const expandedIndices: { [index: number]: boolean } = {};
        for (let index = 0; index < relationshipCount; index++) {
            expandedIndices[index] = !isAllExpanded;
        }
        this.setState({ expandedIndices });
    }

    public render() {
        let self = this;
        if (!self.props.data || !self.props.data.matter || !self.props.data.matter.relationships || self.props.data.loading ) {
            return (
                <SkeletonLoader variant="panel" columns={1} rows={4} wrapInPaper={false} />
            );
        }

        if (self.props.data.matter.relationships) {
            var relationships = self.props.data.matter.relationships.relationships;
            // tslint:disable-next-line:no-any
            var RelationshipIcon: React.ComponentType<{ fontSize?: 'inherit' | 'small' }> = PersonOutlineIcon;
            var isAllExpanded = this.isAllExpanded(relationships.length);
            return (
                <div>
                    <ExpandToggleWrapper>
                        <Button
                            size="small"
                            startIcon={isAllExpanded ? <UnfoldLessOutlinedIcon fontSize="small" /> : <UnfoldMoreOutlinedIcon fontSize="small" />}
                            onClick={() => this.toggleExpandAll(relationships.length)}
                        >
                            {isAllExpanded ? 'Collapse all' : 'Expand all'}
                        </Button>
                    </ExpandToggleWrapper>
                    {
                        // tslint:disable-next-line:no-any
                        relationships.map((relationship: any, index: number) => {
                            if ( relationship.organisations && relationship.organisations.length > 0) {
                                RelationshipIcon = BusinessOutlinedIcon;
                            } else if (relationship.individuals && relationship.individuals.length === 1) {
                                RelationshipIcon = PersonOutlineIcon;
                            } else if (relationship.individuals && relationship.individuals.length > 1) {
                                RelationshipIcon = PeopleOutlineIcon;
                            }
                            let partyDescription = '';
                            partyDescription = relationship.longDescription ?
                            relationship.longDescription : relationship.description;
                            return (
                                <RelationshipWrapper key={'realtionship-' + index} className="relationship-wrapper">
                                    <AnimatedItem noFill={true} delay={index * 30}>
                                        <ExpansionPanel
                                            expanded={!!this.state.expandedIndices[index]}
                                            onChange={() => this.toggleExpanded(index)}
                                        >
                                            <ExpansionPanelSummary
                                                // tslint:disable-next-line:max-line-length
                                                expandIcon={<PanelIcon />}
                                            >
                                                <PanelTitle>
                                                    <PanelIconChip>
                                                        <RelationshipIcon fontSize="small" />
                                                    </PanelIconChip>
                                                    <RelationshipTypeChip>{humanizeRelationshipCode(relationship.relationship)}</RelationshipTypeChip>
                                                    <PartyDescriptionText>{partyDescription}</PartyDescriptionText>
                                                </PanelTitle>
                                            </ExpansionPanelSummary>
                                            <PanelDetails className="centered">
                                                <RvPartyInfoTable
                                                    key={index}
                                                    showDescription={relationship.description}
                                                    relationship={relationship}
                                                />
                                            </PanelDetails>
                                        </ExpansionPanel>
                                    </AnimatedItem>
                                </RelationshipWrapper>
                            );
                        })
                    }
                </div>
            );
        }

        return <div />;

    }
}

export const MatterPartyInfoData = gql`query MatterPartyInfoData($matterId: Int!) {
    matter(id: $matterId) {
        id,
        relationships(includeClients: true, includeOtherSide: true, includeOtherSideSolicitor: true, includeMiscParties: true, includeRedViewUsers: true) {
      relationships {
            relationship
            relationshipType
            description
            longDescription
            reference
            individuals {
              id
              partyId
              displayName
              matterRole
              matterRoleId
            }
            organisations {
              id
              partyId
              aBN
              aCN
              longName
              matterRoleId
            }
            parties {
              id
              partyId
              partyEnumType
              matterRoleId
              phoneNumbers {
                type
                number
                extension
                sequence
              }
              emailAddresses {
                type
                email
                sequence
              }
              addresses {
                type
                display
                sequence
              }
              relatedParties {
                id
                partyEnumType
                phoneNumbers {
                  type
                  number
                  extension
                  sequence
                }
                emailAddresses {
                  type
                  email
                  sequence
                }
                addresses {
                  type
                  display
                  sequence
                }
              }
              relatedIndividuals {
                id
                displayName
              }
              relatedOrganisations {
                id
                aBN
                aCN
                longName
              }
            }
          }
    }
  }
}`;

// tslint:disable-next-line:no-any
export const Parties = graphql<any, PartiesInfoProps, any>
    (MatterPartyInfoData, {
        // Was inheriting the app's global default (cache-and-network) - always re-fetching on
        // every tab mount regardless of cache. Cached after the first load; the header's
        // Refresh button (MatterDetails.tsx) forces a re-fetch via MATTER_DETAILS_REFRESH_EVENT.
        options: ({ matterId }) => ({ variables: { matterId: matterId }, fetchPolicy: 'cache-first' })
    })
    (PartiesInfo);